import type { FeatureFlagKey } from './feature-flag-key.ts';

export interface ResolvedFlag {
  key: string;
  name: string;
  enabled: boolean;
}

export interface FeatureFlagsOptions {
  apiUrl?: string;
  /**
   * The active workspace id. Required for workspace-scoped flags to resolve;
   * pass null when there is no workspace context (workspace-scoped flags then
   * resolve to false).
   */
  workspaceId?: string | null;
}

const DEFAULT_API_URL = 'http://localhost:3002/graphql';

const RESOLVED_FLAGS_QUERY = `
  query ResolvedFlags($workspaceId: ID) {
    resolvedFlags(workspaceId: $workspaceId) {
      key
      name
      enabled
    }
  }
`;

export class FeatureFlagsClient {
  private flags: Map<string, boolean> | null = null;
  private loadedFor: string | null | undefined = undefined;

  get initialized(): boolean {
    return this.flags !== null;
  }

  /**
   * The workspace id the currently-resolved flags were loaded against
   * (`undefined` until the first init). Lets consumers detect a stale set
   * after a workspace switch without re-initializing on every remount.
   */
  get loadedWorkspaceId(): string | null | undefined {
    return this.loadedFor;
  }

  async init(bearerToken: string, options?: FeatureFlagsOptions): Promise<void> {
    const apiUrl = options?.apiUrl ?? DEFAULT_API_URL;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bearerToken}`,
      },
      body: JSON.stringify({
        query: RESOLVED_FLAGS_QUERY,
        variables: { workspaceId: options?.workspaceId ?? null },
      }),
    });

    if (!response.ok) {
      throw new Error(`Feature flags API returned ${response.status}`);
    }

    const json = (await response.json()) as {
      errors?: { message: string }[];
      data: { resolvedFlags: ResolvedFlag[] };
    };

    const firstError = json.errors?.[0];
    if (firstError) {
      throw new Error(`Feature flags GraphQL error: ${firstError.message}`);
    }

    const resolvedFlags: ResolvedFlag[] = json.data.resolvedFlags;
    this.flags = new Map(resolvedFlags.map((f) => [f.key, f.enabled]));
    this.loadedFor = options?.workspaceId ?? null;
  }

  isFeatureFlagEnabled(key: FeatureFlagKey): boolean {
    if (!this.flags) {
      throw new Error('FeatureFlags has not been initialized. Call FeatureFlags.init() first.');
    }
    return this.flags.get(key) ?? false;
  }

  reset(): void {
    this.flags = null;
    this.loadedFor = undefined;
  }
}

/** Module-level singleton shared by every consumer in a process. */
export const FeatureFlags = new FeatureFlagsClient();
